import './StatusDot.scss'
import type { ComponentProps, ReactNode } from 'react'
import { clx } from '../clx'
import { Tooltip } from '../Tooltip'
import type { StatusDotTone } from './tones'

export interface StatusDotProps extends Omit<
  ComponentProps<'span'>,
  'children' | 'aria-label' | 'role'
> {
  tone: StatusDotTone
  /** Accessible name, always required: the colour alone carries no meaning. */
  label: string
  /** Hover/focus text (the portal shows `record.status_comment`). Makes the dot focusable. */
  tooltip?: ReactNode
  /** Pulse animation (the profile dots). Off under prefers-reduced-motion. */
  isPulsing?: boolean
}

/**
 * A small round indicator, exposed as `role="img"` named by `label`. Use
 * `profileStatusTone()` and `stageTone()` to map API values to a tone.
 */
export function StatusDot({
  tone,
  label,
  tooltip,
  isPulsing = false,
  className,
  ref,
  ...rest
}: StatusDotProps) {
  const hasTooltip = tooltip !== undefined && tooltip !== null && tooltip !== ''
  const dot = (
    <span
      ref={ref}
      {...rest}
      role="img"
      aria-label={label}
      {...(hasTooltip && { tabIndex: 0 })}
      className={clx(
        'status-dot',
        `status-dot--as-${tone}`,
        isPulsing && 'status-dot--is-pulsing',
        hasTooltip && 'status-dot--has-tooltip',
        className
      )}
    />
  )
  return hasTooltip ? <Tooltip content={tooltip}>{dot}</Tooltip> : dot
}
